import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Users, Building2, Plus, Edit2, Trash2, FolderTree } from "lucide-react";
import api from "../../lib/api";


const GroupsDepartmentsPage = () => {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ name: "", description: "", group_type: "department", tenant_id: "" });
  const [tenants, setTenants] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, tenantsRes] = await Promise.all([
        api.get(`/admin/groups${filter ? `?group_type=${filter}` : ''}`),
        api.get("/tenants"),
      ]);
      setGroups(groupsRes.data.groups || []);
      setStats(groupsRes.data.stats || {});
      setTenants(tenantsRes.data.tenants || tenantsRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/admin/groups", form);
      toast.success("Group created");
      setShowCreate(false);
      setForm({ name: "", description: "", group_type: "department", tenant_id: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete("/admin/groups/${id}");
      toast.success("Group deleted");
      fetchData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Groups & Departments">
    <div className="p-6 space-y-6" data-testid="groups-departments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Groups & Departments</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage organizational structure and group enrollments</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-group-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Group
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Groups", value: stats.total_groups || 0, color: "#1B4F8A" },
          { label: "Departments", value: stats.departments || 0, color: "#0E8A55" },
          { label: "Teams", value: stats.teams || 0, color: "#F5841F" },
          { label: "Total Members", value: stats.total_members || 0, color: "#7C3AED" },
        ].map((s, i) => (
          <div key={i} className="bg-background border rounded-xl p-4" style={{ borderTop: `3px solid ${s.color}` }}>
            <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">{s.label}</p>
            <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select className="h-9 rounded-md border px-3 text-sm bg-background" value={filter} onChange={e => setFilter(e.target.value)} data-testid="group-type-filter">
          <option value="">All Types</option>
          <option value="department">Departments</option>
          <option value="team">Teams</option>
        </select>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCreate(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="create-group-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Create Group</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Operations & Maintenance" className="mt-1" data-testid="group-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional description" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.group_type} onChange={e => setForm({...form, group_type: e.target.value})}>
                  <option value="department">Department</option>
                  <option value="team">Team</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Tenant (optional)</label>
                <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.tenant_id} onChange={e => setForm({...form, tenant_id: e.target.value})}>
                  <option value="">Global (All Tenants)</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleCreate} data-testid="confirm-create-group-btn">Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Name</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Type</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Members</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Tenant</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Status</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                <FolderTree className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="font-medium">No groups or departments yet</p>
                <p className="text-sm mt-1">Create your first organizational group</p>
              </td></tr>
            ) : groups.map(g => (
              <tr key={g.id} className="border-t hover:bg-muted/30 transition-colors" data-testid={`group-row-${g.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {g.group_type === "department" ? <Building2 className="w-4 h-4 text-blue-600" /> : <Users className="w-4 h-4 text-orange-600" />}
                    <div>
                      <p className="font-semibold">{g.name}</p>
                      {g.description && <p className="text-xs text-muted-foreground">{g.description}</p>}
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase ${g.group_type === 'department' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>{g.group_type}</span>
                </td>
                <td className="p-3 font-medium">{g.member_count || 0}</td>
                <td className="p-3 text-muted-foreground">{g.tenant_id || "Global"}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase ${g.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {g.is_active !== false ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(g.id)} className="text-red-600 hover:text-red-700 h-8" data-testid={`delete-group-${g.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </AdminLayout>
  );
};

export default GroupsDepartmentsPage;
